import { createSlice } from '@reduxjs/toolkit';

import preloaded from '~/preloaded';

import type { PayloadAction } from '@reduxjs/toolkit';
import type L from 'leaflet';
import type { BUILDINGS, IBuildingClickAction } from '~/Actions/ActionSupply';
import type { RegularRewardsType } from '~/Reducers/ReducerTreasury';
import type { BATTLE_TYPES, SEASON_TYPES, SOCIAL_NETWORKS } from '~/constants';
import type { IAchievementsResponse } from '~/queries/achievements';
import type { IBattleTypeData, IBattleTypesData } from '~/queries/battleTypes';
import type { IBattleType, PreModerationField } from '~/types/declaration';
import type { IEmptyObject, Nullable } from '~/types/generics';
import type { IPatrolsPreloadedState } from '~/types/patrols';

export type LadderSeasonLeagueDivision = {
  icons: {
    default: string;
    small: string;
  };
  number: number;
  points: [number, number];
  title: string;
};

export type LadderSeasonLeagueIcons = {
  assembled: string;
  assembled_medium: string;
  assembled_small: string;
  default: string;
  progress_stub: string;
  progress_stub_small: string;
  ribbon: string;
  ribbon_small: string;
  ribbon_wide: string;
  ribbon_wide_small: string;
  small: string;
};

export type ILadderSeasonLeague = {
  color: string;
  divisions: LadderSeasonLeagueDivision[];
  icons: LadderSeasonLeagueIcons;
  localization: {
    mark_secondary: Record<string, string>;
    mark: Record<string, string>;
  };
  number: number;
  points: [number, number];
  progress_color: string;
  range?: unknown[];
};

export type LadderSeasonAdditionalIcons = {
  kind: SEASON_TYPES;
  stars: {
    defeat: string;
    defeat_small: string;
    demotion_defeat: string;
    demotion_victory: string;
    draw: string;
    draw_small: string;
    empty: string;
    empty_small: string;
    promotion_defeat: string;
    promotion_victory: string;
    victory: string;
    victory_small: string;
  };
};

export type ILadderSeason = {
  additional_icons: LadderSeasonAdditionalIcons;
  division_points: number;
  finish_time: number;
  id: number;
  kind: SEASON_TYPES;
  ladder_settings: {
    first_place_achievement_id: number;
  };
  leagues: Record<string, ILadderSeasonLeague>;
  realms: string[];
  localization: {
    title: Record<string, string>;
  };
  ship_level_max: number;
  ship_level_min: number;
  start_time: number;
};

type IAchievement = {
  group_name: string;
  icons: {
    default: string;
    inactive: string;
    local_normal: string;
    normal: string;
  };
  localization: {
    battle_restriction: {
      [lang: string]: string;
    };
    description: {
      [lang: string]: string;
    };
    mark: {
      [lang: string]: string;
    };
    receiving_restriction: {
      [lang: string]: string;
    };
    type: {
      [lang: string]: string;
    };
  };
  grouping: {
    local_rank: string;
  };
  battleTypes?: IBattleType[];
};

export type IAchievements = {
  [cd: string]: IAchievement;
};

export type ISupplyBuildings = {
  [key in BUILDINGS]: ISupplyBuilding;
};

export type ISupplyBuildingLevel = {
  action: Nullable<IBuildingClickAction>;
  geo: Nullable<{
    contour: Nullable<L.LatLngExpression[][]>;
    edges: Nullable<L.LatLngExpression[][]>;
  }>;
  markerPosition: L.LatLngTuple;
  meta: {
    accumulativeResourceRequired: number;
    bonus: string;
    buyable: boolean;
    cost: number;
    function: string;
    levelRequirements: Nullable<{
      type?: string;
      battleCount?: number;
    }>;
    totalBonus: string;
  };
  title: string;
  url: string;
};

export type ISupplyBuildingLevelTemp = ISupplyBuildingLevel['meta'] & { level: number };

export type ISupplyBuilding = {
  id: number;
  isBuildingComplete?: boolean;
  layerOrder: number;
  level: number;
  levels: Record<number, ISupplyBuildingLevel>;
  markerPosition?: L.LatLngTuple;
  maxLevel: number;
  modifiers?: number[];
  name: BUILDINGS;
  order?: number;
  sort?: number;
  sortOrder: number;
  title: Nullable<string>;
  type: BUILDINGS;
};

export enum ENTRY_SOURCE {
  CLAN_HUB = 'clan_hub',
  CLAN_PROFILE = 'clan_profile',
  CLAN_RECOMMENDATIONS = 'clan_recommendations',
  CLAN_SEARCH = 'clan_search',
  PLAYER_SEARCH = 'player_search',
  RECRUITSTATION = 'recruitstation',
  SIDEBAR = 'sidebar',
  PROFILE = 'profile',
  ABOUT = 'about',
}

export type ISettings = {
  hasCrossRealm: boolean;
  isCISRegion: boolean;
  preModerationFields: PreModerationField[];
  achievements: {
    data: IAchievements;
    isEnabled: boolean;
  };
  treasury?: RegularRewardsType;
  ajaxTools: {
    accountInfoSyncTimeout: number;
    pendingOperationTimeout: number;
    pendingOperationWait: number;
  };
  battleTypes: Array<{
    content: string;
    value: BATTLE_TYPES;
  }>;
  battleTypesData?: IBattleTypesData;
  clanCreate: {
    cost: number;
    discount: number;
    nameMaxLength: number;
    nameMinLength: number;
    tagMaxLength: number;
    tagMinLength: number;
    tagPattern: string;
  };
  clanLeaveCooldown: number;
  clanMembersLimit: number;
  clanRename: {
    allowed: boolean;
    cost: number;
    renameCooldown: number;
  };
  clanrec2?: {
    disableForNonInsiders: boolean;
    isEnabled: boolean;
    languages: string[];
    showRecommendationsForClansMembers: boolean;
  };
  darwin: {
    isEnabled: boolean;
    grandFleetAchievementCd: number;
    syncTime: string;
  };
  clanhub: {
    isEnabled: boolean;
  };
  clanstars?: {
    isEnabled: boolean;
  };
  communityServices: SOCIAL_NETWORKS[];
  csrfToken: string;
  cwars: {
    isEnabled: boolean;
    isRulesShown: boolean;
    features: {
      additionalAttempts: boolean;
      additionalAttemptsPreparation: boolean;
      bonusCells: boolean;
      enemyResults: boolean;
      groupRounds: boolean;
    };
    war_table?: {
      columns: string[];
    };
  };
  defaultBattleType: BATTLE_TYPES;
  editClanColor: {
    colorMaxLightness: number;
    colorMinLightness: number;
  };
  editClanDescription: {
    maxChars: number;
    allowed: boolean;
  };
  entries: {
    limit: number;
  };
  entrySources: Record<ENTRY_SOURCE, string>;
  excludedRealms: string[];
  patrols: IPatrolsPreloadedState | IEmptyObject;
  fetchSpinnerDelay: number;
  formats: {
    date: string;
    date_django: string;
    datetime: string;
    decimal: string;
    thousand: string;
    time: string;
  };
  isEnabledPreloadImages: boolean;
  ingameUserAgent: string;
  ladder: {
    hallOfFameIsEnabled?: boolean;
    isCrossRealmLadderByDefault: boolean;
    isCrossRealmLadderEnabled: boolean;
    isEnabled: boolean;
    lastSeasonId: number;
    lastSeasonIgnoreTimeCondition?: boolean;
    seasons?: Record<number, ILadderSeason>;
  };
  mediaPath: string;
  notificationTimeout: number;
  permissions: {
    combat_officer: string[];
    commander: string[];
    executive_officer: string[];
    intelligence_officer: string[];
    junior_officer: string[];
    personnel_officer: string[];
    private: string[];
    quartermaster: string[];
    recruit: string[];
    recruitment_officer: string[];
    reservist: string[];
  };
  profileUrl: string;
  realm: {
    code: string;
    languageCode: string;
    languageCodeGloss: string;
  };
  realmsMap: {
    [key: string]: { shortName: string; name: string };
  };
  urlByRealm: { [key: string]: string };
  recommendations: {
    battleTypes: {
      cvc: string;
      oper: string;
      pvp: string;
    };
    isEnabled: boolean;
    languages: {
      [key: string]: string;
    };
    metricTypes: {
      average_oper_battles: string;
      average_ship_level: string;
      average_stars: string;
      cvc_game_days: string;
    };
    metricsInfo: {
      average_oper_battles: string;
      cvc_game_days: string;
    };
    showRecommendationsForClansMembers: number;
    userSettingsMapping: unknown;
  };
  releaseFeaturesDates: {
    clanrec2?: string;
    clanstars?: string;
    darwin?: string;
    recommendations: string;
  };
  search: {
    autocompleteMaxResult: number;
    limit: number;
    maxChars: number;
    minChars: number;
    searchResultMaxItems: number;
  };
  seasonNames: Record<number, string>;
  sentryUrl: string | null;
  supply: {
    buildings: Partial<ISupplyBuildings>;
    clanLevelingToAccountLeveling: Record<number, number>;
    clanLevelingToResource: Record<number, number>;
    isEnabled: boolean;
    maxZoom: number;
    minZoom: number;
    multiTeamsModifier: number;
    releaseDate: string;
    tileUrlTemplate: string;
    tilesLocalExcludes: string[];
    tilesLocalPath: string;
    tilingServiceHost: string;
    tilingServiceSubdomains: string[];
  };
  treasuryMinDaysInClan: number;
  treasuryMinDistributionSumm: number;
};

export const initialState: ISettings = preloaded.settings;

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    setBattleTypes(state: ISettings, action: PayloadAction<IBattleTypeData[]>) {
      state.battleTypesData = action.payload.reduce((acc, item) => {
        return { ...acc, [item.name.toLowerCase()]: item };
      }, {});
    },

    setAchievementBattleType(state: ISettings, action: PayloadAction<IAchievementsResponse>) {
      action.payload.achievements.forEach((achievement) => {
        if (state.achievements.data[achievement.id]) {
          state.achievements.data[achievement.id].battleTypes = achievement.battleTypes.map((battleType) => {
            return battleType.name.toLowerCase() as IBattleType;
          });
        }
      });
    },

    setClanHubEnable(state: ISettings, action: PayloadAction<boolean>) {
      state.clanhub.isEnabled = action.payload;
    },

    syncSetting(state: ISettings) {
      return { ...state };
    },
  },
});

export const settingsActions = settingsSlice.actions;

export default settingsSlice.reducer;
